import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePageTitle } from '../hooks/usePageTitle';

const T = {
  sectionTitle: { fontSize: 'var(--t-label)', color: 'var(--ink-faint)', letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: '0.75rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--rule)' },
  body:         { fontSize: 'var(--t-body)', color: 'var(--ink-mid)', lineHeight: 'var(--body-height)' },
  meta:         { fontSize: 'var(--t-meta)', color: 'var(--ink-faint)' },
};

const statusLabel = { live: 'Live', in_development: 'In development', shipped: 'Shipped' };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const isMobile = useIsMobile();
  usePageTitle('Projects');

  useEffect(() => {
    supabase.from('portfolio_projects').select('*').order('display_order')
      .then(({ data }) => setProjects(data || []));
  }, []);

  return (
    <main style={{ maxWidth: '800px', margin: '0 auto', padding: isMobile ? '1.5rem 1.25rem 4rem' : '2.5rem 2.5rem 6rem' }}>

      <p style={{ fontSize: '11px', color: 'var(--ink-faint)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Independent Projects</p>
      <h1 style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: 500, marginBottom: '1.5rem', lineHeight: 1.2 }}>Apps & Tools</h1>
      <div style={{ maxWidth: '540px', marginBottom: '2.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <p style={T.body}>I'm currently developing a small portfolio of independent mobile applications, focused on practical, real-world use cases across safety, social discovery, and creative workflows.</p>
        <p style={T.body}>These projects are part of a broader effort to strengthen my capabilities in product development, data, and engineering. I'm working with a modern stack including React Native, TypeScript, and Supabase, while also exploring the role of AI in shaping more responsive, useful user experiences.</p>
        <p style={T.body}>Alongside the technical work, each project is approached with a commercial lens. From early concept through to launch, the focus is on building products that are clear in purpose, considered in design, and grounded in real user needs.</p>
      </div>

      {projects.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {projects.map((project, i) => (
            <div key={project.id} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '180px 1fr auto',
              gap: isMobile ? '0.25rem' : '0 2.5rem',
              padding: '1.25rem 0',
              borderTop: '1px solid var(--rule)',
              borderBottom: i === projects.length - 1 ? '1px solid var(--rule)' : 'none',
              alignItems: 'start',
            }}>
              <div>
                <p style={{ fontSize: 'var(--t-label)', color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {project.category}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 'var(--t-body)', fontWeight: 500, marginBottom: '0.35rem', color: 'var(--ink)' }}>
                  {project.headline}
                </p>
                {project.description && (
                  <p style={{ ...T.body, maxWidth: '480px' }}>{project.description}</p>
                )}
                {project.tech && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.6rem' }}>
                    {project.tech.split(',').map(t => (
                      <span key={t} style={{ fontSize: '10px', color: 'var(--ink-faint)', border: '1px solid var(--rule)', padding: '0.15rem 0.5rem' }}>
                        {t.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ paddingTop: isMobile ? '0.4rem' : '0.1rem' }}>
                <span style={{
                  fontSize: '10px',
                  color: project.status === 'live' ? 'var(--ink)' : 'var(--ink-faint)',
                  border: '1px solid',
                  borderColor: project.status === 'live' ? 'var(--ink)' : 'var(--rule)',
                  padding: '0.2rem 0.5rem',
                  whiteSpace: 'nowrap',
                }}>
                  {statusLabel[project.status] || project.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ ...T.body }}>More projects coming soon.</p>
      )}

    </main>
  );
}
